import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  from,
  fromEvent,
  map,
  merge,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AuthActions } from './auth.actions';
import {
  AUTH_CONFIG,
  AuthConfig,
  UserProfile,
} from '@lyceo/shared/auth/util-models';
import { AuthService } from '../../auth.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { distinctUntilChanged, filter, startWith } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ApplicationActions } from '@lyceo/shared/data-access';
import { setUser } from '@sentry/capacitor';

@Injectable()
export class AuthEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly authService: AuthService = inject(AuthService);
  private readonly oAuthService: OAuthService = inject(OAuthService);
  private readonly router: Router = inject(Router);
  private readonly authConfig: AuthConfig = inject<AuthConfig>(AUTH_CONFIG);

  public storageEvents$ = createEffect(() => {
    return fromEvent(window, 'storage').pipe(
      filter((event): event is StorageEvent => event instanceof StorageEvent),
      filter((event) => event.key === 'access_token' || event.key !== null),
      tap(() => {
        if (this.oAuthService.showDebugInformation) {
          console.warn(
            'Noticed changes to access_token (most likely from another tab)',
          );
        }
      }),
      map(() => {
        if (this.oAuthService.hasValidAccessToken()) {
          return AuthActions.storageTokenValid();
        }
        return AuthActions.storageTokenInvalid();
      }),
    );
  });

  public isAuthenticated$ = createEffect(() => {
    return merge(this.oAuthService.events, fromEvent(window, 'storage')).pipe(
      map(() => this.oAuthService.hasValidAccessToken()),
      startWith(this.oAuthService.hasValidAccessToken()),
      distinctUntilChanged(),
      map((isAuthenticated) =>
        AuthActions.updateAuthenticated({ isAuthenticated }),
      ),
    );
  });

  public oAuthEventErrors$ = createEffect(() => {
    return this.oAuthService.events.pipe(
      filter((event) =>
        [
          'session_terminated',
          'session_error',
          'user_profile_load_error',
        ].includes(event.type),
      ),
      map(() => AuthActions.sessionErrorLogout()),
    );
  });

  public init$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ApplicationActions.init),
      filter(() => this.authConfig.initialize),
      map(() => AuthActions.loadDiscoveryDocument()),
    );
  });

  public loadDiscoveryDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuthActions.loadDiscoveryDocument,
        AuthActions.retryLoadDiscoveryDocument,
      ),
      switchMap(() =>
        from(this.oAuthService.loadDiscoveryDocument()).pipe(
          map(() => AuthActions.loadDiscoveryDocumentSuccess()),
          catchError(() => of(AuthActions.loadDiscoveryDocumentFailure())),
        ),
      ),
    );
  });

  public loadDiscoveryDocumentSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loadDiscoveryDocumentSuccess),
      switchMap(() => {
        if (
          !this.oAuthService.hasValidAccessToken() &&
          !!this.oAuthService.getRefreshToken()
        ) {
          return from(this.oAuthService.refreshToken()).pipe(
            map(() => AuthActions.refreshingTokenSuccess()),
            catchError(() => of(AuthActions.refreshingTokenFailure())),
          );
        }

        return of(AuthActions.refreshingTokenNotNeeded());
      }),
    );
  });

  public automaticRefresh$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.refreshingTokenSuccess,
          AuthActions.refreshingTokenNotNeeded,
        ),
        tap(() => this.oAuthService.setupAutomaticSilentRefresh()),
      );
    },
    { dispatch: false },
  );

  public tryLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuthActions.refreshingTokenSuccess,
        AuthActions.refreshingTokenNotNeeded,
      ),
      switchMap(() =>
        from(
          this.oAuthService.tryLogin({ preventClearHashAfterLogin: true }),
        ).pipe(
          map(() => {
            if (this.oAuthService.hasValidAccessToken()) {
              return AuthActions.tryLoginSuccess();
            }

            return AuthActions.tryLoginFailure();
          }),
          catchError(() => of(AuthActions.tryLoginFailure())),
        ),
      ),
    );
  });

  public checkState$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.tryLoginSuccess),
        tap(() => {
          this.authService.checkState();
        }),
      );
    },
    { dispatch: false },
  );

  public mobileLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.mobileTryLogin),
      switchMap((action) =>
        from(
          this.oAuthService.tryLogin({
            customHashFragment: '#' + action.urlSearch,
          }),
        ).pipe(
          map(() => {
            if (this.oAuthService.hasValidAccessToken()) {
              return AuthActions.mobileTryLoginSuccess();
            }

            return AuthActions.mobileTryLoginFailure();
          }),
          catchError(() => of(AuthActions.mobileTryLoginFailure())),
        ),
      ),
    );
  });

  public initUserProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.tryLoginSuccess, AuthActions.mobileTryLoginSuccess),
      switchMap(() =>
        from(this.authService.userProfile()).pipe(
          map((request) =>
            AuthActions.loadUserProfileSuccess({
              profile: (request as { info: UserProfile }).info,
            }),
          ),
          catchError((error) =>
            of(AuthActions.loadUserProfileFailure({ error })),
          ),
        ),
      ),
    );
  });

  public login$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.login),
        tap((action) => {
          this.authService.login(action.kcIdpHint);
        }),
      );
    },
    { dispatch: false },
  );

  public logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.userLogout, AuthActions.sessionErrorLogout),
      switchMap(() =>
        from(this.authService.logout()).pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError(() => of(AuthActions.logoutFailure())),
        ),
      ),
    );
  });

  public logoutWithoutRevoke$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        AuthActions.storageTokenInvalid,
        AuthActions.refreshingTokenFailure,
        AuthActions.logoutFailure,
      ),
      map(() => {
        this.authService.logoutWithoutRevoke();
        return AuthActions.logoutSuccess();
      }),
    );
  });

  public cleanUpUrl$ = createEffect(
    () => {
      return this.oAuthService.events.pipe(
        filter((event) => event.type === 'token_received'),
        map(() => {
          this.router.navigate([location.pathname], {
            queryParams: {},
            queryParamsHandling: 'preserve',
          });
        }),
      );
    },
    { dispatch: false },
  );

  public mobileLogoutFinish$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.mobileLogoutFinish),
        tap(() => {
          this.router.navigate(['/login']);
        }),
      );
    },
    { dispatch: false },
  );

  public mobileLoginSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.mobileTryLoginSuccess),
        tap(() => {
          this.router.navigate(['/home']);
        }),
      );
    },
    { dispatch: false },
  );

  public logoutDone$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logoutSuccess, AuthActions.logoutFailure),
        tap(() => {
          if (Capacitor.getPlatform() === 'android') {
            return;
          }
          Browser.close().catch(() => {
            /* an error is thrown when nothing can be closed, this is fine */
          });
        }),
      );
    },
    { dispatch: false },
  );

  public setSentryUser$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loadUserProfileSuccess),
        filter(() => this.authConfig.setSentryUser),
        tap((action) => {
          setUser({
            id: action.profile.person_id,
          });
        }),
      );
    },
    { dispatch: false },
  );
}
